/**
 * Mother Milk API Service
 *
 * Provides data-access methods for the Mother Milk tab.
 * Calls tms-api-inventoryManagement (/api/v1/milk-receivables) with
 * source_type = 'MOTHER'.
 *
 * Field mapping between the UI view-model and the REST API:
 *   containerId   ↔  receipt_number
 *   motherName    ↔  provider_first_name + provider_last_name (falls back to provider_id UUID)
 *   volumeMl      ↔  volume_ml
 *   pumpedDate    ↔  received_at
 *   expiryDate    ↔  expiry_at
 *   status        ↔  milk_status  (backend enum: FRESH / FROZEN / THAWED)
 *   milkType      ↔  milk_type    (backend enum: PRETERM / TERM / FOREMILK / HINDMILK / SKIM_HUMAN_MILK / COLOSTRUM)
 *   location      ↔  location
 */
import type {
  MotherMilkContainer,
  MotherMilkQueryParams,
  MilkReceivableMilkStatus,
  MilkReceivableMilkType,
  BulkReceiveFailure,
} from '../types';
import type { ApiMilkReceivable } from '../types';
import {
  listMilkReceivables,
  getMilkReceivableById,
  createMilkReceivable,
  createMilkReceivablesBulk,
  updateMilkReceivable,
  discardMilkReceivable,
} from './milk-receivables.api';
import { config } from '@/core/config';

// ── Date formatting helper ────────────────────────────────────────────────────

export { formatMilkDate, formatMilkDateTime } from './milk-date-format';

// ── Response → view-model ─────────────────────────────────────────────────────

function toMotherMilkContainer(rec: ApiMilkReceivable): MotherMilkContainer {
  return {
    id: rec.receivable_id,
    containerId: rec.receipt_number,
    motherName: `${rec.provider_first_name} ${rec.provider_last_name}`.trim() || rec.provider_id,
    volumeMl: parseFloat(rec.volume_ml),
    remainingVolumeMl: parseFloat(rec.remaining_volume_ml),
    pumpedDate: rec.received_at,
    expiryDate: rec.expiry_at ?? null,
    milkType: rec.milk_type ?? 'TERM',
    status: rec.milk_status ?? 'FRESH',
    bottleStatus: rec.status,
    location: rec.location ?? '',
    createdAt: rec.created_at,
    updatedAt: rec.updated_at,
  };
}

// ── Public API ────────────────────────────────────────────────────────────────

export interface CreateMotherMilkContainerPayload {
  containerId: string;
  /**
   * TODO: replace with a provider UUID lookup so the container is attributed to
   * the selected mother rather than the logged-in staff member.
   * Until the provider-search feature is implemented, provider_id falls back
   * to config.staffId and the name is derived from the provider record on read.
   */
  providerId?: string;
  volumeMl: number;
  milkType: MilkReceivableMilkType;
  status: MilkReceivableMilkStatus;
  location: string;
  /** storage_location_id of the selected Location, resolved by name from the storage-locations list */
  storageLocationId?: string;
  pumpedDate: string;
  expiryDate: string;
}

/**
 * Fetch paginated, filtered mother milk containers from the inventory management service.
 */
export async function fetchMotherMilkContainers(
  params: MotherMilkQueryParams,
): Promise<{ items: MotherMilkContainer[]; total: number }> {
  if (!config.hospitalId) {
    throw new Error('fetchMotherMilkContainers: VITE_HOSPITAL_ID is not configured');
  }

  const data = await listMilkReceivables({
    hospital_id: config.hospitalId,
    source_type: 'MOTHER' as const,
    milk_status: params.status?.length ? params.status.join(',') : undefined,
    milk_type: params.milkType?.length ? params.milkType.join(',') : undefined,
    search: params.search?.trim() || undefined,
    page: params.page ?? 1,
    limit: params.pageSize ?? 25,
  });

  return {
    items: data.items.map(toMotherMilkContainer),
    total: data.pagination.totalItems,
  };
}

/**
 * Fetch a single mother milk container by its receivable ID.
 * Called when opening the Edit/Delete modal to ensure the latest data is shown.
 */
export async function fetchMotherMilkContainerById(id: string): Promise<MotherMilkContainer> {
  const rec = await getMilkReceivableById(id, 'MOTHER');
  return toMotherMilkContainer(rec);
}

/**
 * Delete a mother milk container by receivable ID (soft-delete / discard).
 */
export async function deleteMotherMilkContainer(id: string, reason?: string): Promise<boolean> {
  await discardMilkReceivable(id, {
    notes: reason?.trim() || 'Discarded via Mother Milk inventory screen',
  }, 'MOTHER');
  return true;
}

export interface UpdateMotherMilkContainerPayload {
  volumeMl: number;
  milkType: MilkReceivableMilkType;
  status: MilkReceivableMilkStatus;
  location: string;
  storageLocationId?: string;
  pumpedDate: string;
  expiryDate: string;
}

/**
 * Update an existing mother milk container.
 */
export async function updateMotherMilkContainer(
  id: string,
  payload: UpdateMotherMilkContainerPayload,
): Promise<MotherMilkContainer | null> {
  if (!config.hospitalId) {
    throw new Error('updateMotherMilkContainer: VITE_HOSPITAL_ID is not configured');
  }

  const rec = await updateMilkReceivable(id, {
    hospital_id: config.hospitalId,
    volume_ml: payload.volumeMl,
    milk_type: payload.milkType,
    milk_status: payload.status,
    location: payload.location,
    storage_location_id: payload.storageLocationId,
    received_at: payload.pumpedDate,
    expiry_at: payload.expiryDate || null,
  }, 'MOTHER');
  return toMotherMilkContainer(rec);
}

/**
 * Create a new mother milk container (receivable).
 */
export async function createMotherMilkContainer(
  payload: CreateMotherMilkContainerPayload,
): Promise<MotherMilkContainer> {
  if (!config.hospitalId) {
    throw new Error('createMotherMilkContainer: VITE_HOSPITAL_ID is not configured');
  }
  if (!config.receivedBy) {
    throw new Error('createMotherMilkContainer: VITE_RECEIVED_BY (or VITE_STAFF_ID) is not configured');
  }
  const resolvedProviderId = payload.providerId ?? config.staffId;
  if (!resolvedProviderId) {
    throw new Error('createMotherMilkContainer: provider ID is unavailable and VITE_STAFF_ID is not configured');
  }

  const rec = await createMilkReceivable({
    hospital_id: config.hospitalId,
    receipt_number: payload.containerId,
    source_type: 'MOTHER',
    // Use explicit providerId or fallback to configured staff ID
    provider_id: resolvedProviderId,
    received_by: config.receivedBy,
    volume_ml: payload.volumeMl,
    milk_type: payload.milkType,
    milk_status: payload.status,
    location: payload.location,
    storage_location_id: payload.storageLocationId ?? null,
    received_at: payload.pumpedDate,
    expiry_at: payload.expiryDate || null,
  });
  return toMotherMilkContainer(rec);
}

/**
 * Create multiple mother milk containers in a single API call.
 * Sends all bottles in one POST /api/v1/milk-receivables request.
 */
export async function createMotherMilkContainersBulk(
  payloads: CreateMotherMilkContainerPayload[],
): Promise<{ containers: MotherMilkContainer[]; failed: BulkReceiveFailure[] }> {
  if (!config.hospitalId) {
    throw new Error('createMotherMilkContainersBulk: VITE_HOSPITAL_ID is not configured');
  }
  if (!config.receivedBy) {
    throw new Error('createMotherMilkContainersBulk: VITE_RECEIVED_BY (or VITE_STAFF_ID) is not configured');
  }

  const hospitalId = config.hospitalId;
  const receivedBy = config.receivedBy;

  const apiPayloads = payloads.map((payload) => {
    const resolvedProviderId = payload.providerId ?? config.staffId;
    if (!resolvedProviderId) {
      throw new Error('createMotherMilkContainersBulk: provider ID is unavailable and VITE_STAFF_ID is not configured');
    }
    return {
      hospital_id: hospitalId,
      receipt_number: payload.containerId,
      source_type: 'MOTHER' as const,
      provider_id: resolvedProviderId,
      received_by: receivedBy,
      volume_ml: payload.volumeMl,
      milk_type: payload.milkType,
      milk_status: payload.status,
      location: payload.location,
      storage_location_id: payload.storageLocationId ?? null,
      received_at: payload.pumpedDate,
      expiry_at: payload.expiryDate || null,
    };
  });

  const { created, failed } = await createMilkReceivablesBulk(apiPayloads);
  return {
    containers: created.map(toMotherMilkContainer),
    failed: failed.map((f) => ({ containerId: f.receipt_number, reason: f.reason })),
  };
}
